"use client"

import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ThemeProvider } from "next-themes"
import { Toaster } from "sonner"
import { AuthProvider } from "@/contexts/auth-context"
import { RunProvider } from "@/contexts/run-context"
import { useState, type ReactNode } from "react"

interface ProvidersProps {
  children: ReactNode
  initialRunId?: string | null
  initialMode?: "database" | "codebase" | "webservice"
}

export function Providers({ children, initialRunId, initialMode }: ProvidersProps) {
  // Create QueryClient instance once per component lifecycle
  // This ensures proper SSR hydration and prevents shared state across requests
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Default staleTime of 30 seconds - data is fresh for this period
            staleTime: 30 * 1000,
            // Retry once on failure
            retry: 1,
            // Don't refetch on window focus by default (can be overridden per query)
            refetchOnWindowFocus: false,
          },
        },
      })
  )

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RunProvider initialRunId={initialRunId} initialMode={initialMode}>
            {children}
            <Toaster richColors closeButton position="top-right" />
          </RunProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}
